/*----------------------------------
    Appearance Switcher
-----------------------------------*/
(() => {
  const root = document.documentElement;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');

  // Safe localStorage helpers (avoid exceptions in private modes)
  const storage = {
    get(key, fallback = null) {
      try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, value); } catch {}
    }
  };

  function applyTheme(mode) {
    if (mode === 'auto') {
      root.setAttribute('data-theme', mq.matches ? 'dark' : '');
    } else if (mode === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.setAttribute('data-theme', '');
    }
  }

  // Read saved mode (default to auto) and ALWAYS apply it
  const initialMode = storage.get('appearance-mode', 'auto');
  applyTheme(initialMode);

  // Only wire UI if the switcher (and its buttons) exist
  const switcher = document.querySelector('.appearance-switcher');
  if (switcher) {
    const btns = {
      light: switcher.querySelector('.appearance--light'),
      dark: switcher.querySelector('.appearance--dark'),
      auto: switcher.querySelector('.appearance--auto'),
    };

    if (btns.light && btns.dark && btns.auto) {
      function setActive(mode) {
        Object.values(btns).forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        const active = btns[mode];
        if (active) {
          active.classList.add('is-active');
          active.setAttribute('aria-pressed', 'true');
        }
      }

      function setAppearance(mode, save = true) {
        setActive(mode);
        applyTheme(mode);
        if (save) storage.set('appearance-mode', mode);
      }

      // Reflect current mode in UI
      setActive(initialMode);

      // Button handlers
      btns.light.addEventListener('click', () => setAppearance('light'));
      btns.dark.addEventListener('click', () => setAppearance('dark'));
      btns.auto.addEventListener('click', () => setAppearance('auto'));
    }
  }

  // React to OS changes only when in auto mode (works with or without UI)
  const handleOSChange = () => {
    if (storage.get('appearance-mode', 'auto') === 'auto') {
      applyTheme('auto');
    }
  };
  mq.addEventListener?.('change', handleOSChange);
  mq.addListener?.(handleOSChange); // legacy fallback
})();
/*----------------------------------
    Masonry image gallery
-----------------------------------*/
class DecorMasonryPlugin {
  constructor(gallery, options = {}) {
    if (!gallery) return;

    this.gallery = gallery;
    this.options = {
      desktopColumns: 4,
      gap: 16,
      breakpoints: null,
      ...options
    };

    this.resizeHandler = this.onResize.bind(this);
    this.init();
  }

  qsa(root, sel) {
    try {
      return root.querySelectorAll(sel);
    } catch {
      return root.querySelectorAll(sel.replace(':scope > ', ''));
    }
  }

  getDirectCards(onlyVisible = false) {
    let cards = this.qsa(this.gallery, ':scope > .card');
    if (!cards.length) cards = this.gallery.querySelectorAll('.card');
    return Array.from(cards).filter(c => (onlyVisible ? !c.classList.contains('hidden') : true));
  }

  computeColumns() {
    const w = window.innerWidth || document.documentElement.clientWidth || 0;
    const { desktopColumns, breakpoints } = this.options;

    if (breakpoints && typeof breakpoints === 'object') {
      const sorted = Object.entries(breakpoints)
        .map(([bp, cols]) => [parseInt(bp), cols])
        .sort((a, b) => b[0] - a[0]);

      for (const [bp, cols] of sorted) {
        if (w >= bp) return cols;
      }
      return 1;
    }

    if (w >= 1200) return desktopColumns;
    if (w >= 768) return Math.min(3, desktopColumns);
    if (w >= 480) return 2;
    return 1;
  }

  layout() {
    const cards = this.getDirectCards(true);
    if (!cards.length) {
      this.gallery.style.height = '0px';
      return;
    }

    const columns = this.computeColumns();
    // Toggle helper class for single column
    if (columns === 1) {
      this.gallery.classList.add('masonry-single-col');
    } else {
      this.gallery.classList.remove('masonry-single-col');
    }

    const gap = this.options.gap;
    const containerWidth = this.gallery.clientWidth || this.gallery.offsetWidth || 0;
    const cardWidth = (containerWidth - (gap * (columns - 1))) / columns;

    cards.forEach(card => { card.style.width = `${cardWidth}px`; });

    const colHeights = new Array(columns).fill(0);
    cards.forEach(card => {
      const col = colHeights.indexOf(Math.min(...colHeights));
      const x = col * (cardWidth + gap);
      const y = colHeights[col];

      card.style.left = `${x}px`;
      card.style.top = `${y}px`;

      colHeights[col] += card.offsetHeight + gap;
    });

    this.gallery.style.height = `${Math.max(...colHeights, 0)}px`;
  }

  reveal() {
    const cards = this.getDirectCards();
    this.layout();

    cards.forEach((card, i) => {
      card.style.visibility = 'visible';
      card.classList.remove('loaded');
      setTimeout(() => card.classList.add('loaded'), i * 50);
    });
  }

  preloadAndLayout() {
    const cards = this.getDirectCards();
    const images = cards.map(c => c.querySelector('img')).filter(Boolean);
    const total = images.length;

    cards.forEach(c => { c.style.visibility = 'hidden'; });

    if (!total) return this.reveal();

    let loaded = 0;
    const done = () => { if (++loaded === total) this.reveal(); };

    images.forEach(img => {
      if (img.complete) {
        done();
      } else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }

  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      const prevColumns = this._lastColumns || 0;
      const columns = this.computeColumns();
      this._lastColumns = columns;

      if (columns !== prevColumns) {
        const cards = this.getDirectCards(true);
        cards.forEach(card => {
          card.style.width = '';
          card.style.left = '';
          card.style.top = '';
        });
      }
      this.layout();
    }, 150);
  }

  bindResize() {
    window.addEventListener('resize', this.resizeHandler);
  }

  unbindResize() {
    window.removeEventListener('resize', this.resizeHandler);
    clearTimeout(this.resizeTimeout);
  }

  init() {
    this.preloadAndLayout();
    this.bindResize();
  }
}
window.__decorMasonryInstances = new WeakMap();
document.querySelectorAll('.gallery-masonry').forEach(gallery => {
  const instance = new DecorMasonryPlugin(gallery, {
    gap: 16,
    breakpoints: {
      1920: 5,
      1200: 4,
      1024: 3,
      768: 2,
      320: 2
    }
  });
  window.__decorMasonryInstances.set(gallery, instance);
});
/* ===============================
   Filter (reusable, global or scoped)
   =============================== */
(() => {
  'use strict';

  function filterGallery(filterValue, { root = document } = {}) {
    const cards = root.querySelectorAll('.card');
    cards.forEach(card => {
      const cat = card.getAttribute('data-category');
      if (filterValue === 'all' || cat === filterValue) card.classList.remove('hidden');
      else card.classList.add('hidden');
    });

    // re-layout all galleries on the page after 100ms (keeps your original timing)
    setTimeout(() => {
      document.querySelectorAll('.gallery-masonry').forEach(g => {
        const instance = window.__decorMasonryInstances?.get(g);
        if (instance) instance.layout();
      });
    }, 100);
  }

  // click delegation for any [data-filter] element (optional, but handy)
  function bindFilterClicks({ container = document, scope = document } = {}) {
    const onClick = (e) => {
      const el = e.target.closest?.('[data-filter]');
      if (!el) return;
      e.preventDefault();
      const val = el.getAttribute('data-filter') || 'all';
      filterGallery(val, { root: scope });
    };
    container.addEventListener('click', onClick);
    return () => container.removeEventListener('click', onClick);
  }

  window.DecorFilter = { filterGallery, bindFilterClicks };

  // Backward compat: global function
  window.filterGallery = (val) => filterGallery(val);
})();

/* ===============================
   Auto-boot (safe on any page)
   =============================== */
(() => {
  'use strict';
  document.addEventListener('DOMContentLoaded', () => {
    // init every gallery on the page automatically (or do nothing if none exist)
    window.DecorMasonry?.initAll({ gallerySelector: '.gallery-masonry' });

    // wire up any [data-filter] automatically (safe even if none exist)
    window.DecorFilter?.bindFilterClicks({ container: document, scope: document });
  });
})();


/*----------------------------------
    Dropdown Main
-----------------------------------*/
/**
 * UIControls: Modular Tab and Dropdown Component Handler
 * - Supports nested tabs
 * - Multi-select dropdowns
 * - Fully scoped per component
 * - No global leaks
 */
(() => {
  const UIControls = {
    initAll() {
      this.initTabs();
      this.initDropdowns();
    },

    /** Utility Helpers **/
    utils: {
      toggleActive(el, group, attr, target) {
        group.forEach(e => e.classList.remove('is-active'));
        el.classList.add('is-active');
        group.forEach(e => {
          const val = e.getAttribute(attr);
          e.classList.toggle('is-active', val === target);
        });
      },

      clearSelections(dropdown) {
        dropdown.classList.remove('has-selections');
        const label = dropdown.querySelector('.dropdown__label');
        label.textContent = dropdown.dataset.placeholder || 'Select';
        dropdown.querySelectorAll('.dropdown__item--active').forEach(i => i.classList.remove('dropdown__item--active'));
        dropdown._selectedItems = new Map();
      },

      getSelectedMap(dropdown) {
        if (!dropdown._selectedItems) dropdown._selectedItems = new Map();
        return dropdown._selectedItems;
      },

      updateDropdownDirection(dropdown) {
        const toggle = dropdown.querySelector('.dropdown__toggle');
        const menu = dropdown.querySelector('.dropdown__menu');
        if (!toggle || !menu) return;

        dropdown.classList.remove('dropdown--up'); // reset

        const rect = dropdown.getBoundingClientRect();
        const menuHeight = menu.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
          dropdown.classList.add('dropdown--up');
        }
      },

      // --------- PORTAL HELPERS ---------
      portalDropdownMenu(dropdown, menu) {
        if (!menu._originalParent) {
          menu._originalParent = menu.parentNode;
          menu._originalNext = menu.nextSibling;
          menu._ownerDropdown = dropdown; // Track for outside click restore
        }
        // Hide menu until positioned
        menu.style.opacity = '0';
        menu.style.pointerEvents = 'none';
        document.body.appendChild(menu);
        menu.classList.add('dropdown__menu--portal');
      },

      restoreDropdownMenu(menu) {
        if (menu._originalParent) {
          menu._originalParent.insertBefore(menu, menu._originalNext);
          menu.classList.remove('dropdown__menu--portal');
          // Reset all position/dimension styles
          menu.style.position = '';
          menu.style.left = '';
          menu.style.top = '';
          menu.style.zIndex = '';
          menu.style.width = '';
          menu.style.minWidth = '';
          menu.style.maxWidth = '';
          menu.style.opacity = '';
          menu.style.pointerEvents = '';
          menu.style.visibility = '';
          menu.style.display = '';
        }
      },

      positionDropdownMenu(toggle, menu) {
        // TEMP show to measure
        menu.style.visibility = 'hidden';
        menu.style.display = 'block';
        menu.style.left = '0';
        menu.style.top = '0';

        const rect = toggle.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset;
        const scrollX = window.scrollX || window.pageXOffset;
        const menuRect = menu.getBoundingClientRect();

        const viewportPadding = 8;

        let top = rect.bottom + scrollY;
        let left = rect.left + scrollX;

        if (window.innerHeight - rect.bottom < menuRect.height + viewportPadding &&
          rect.top > menuRect.height + viewportPadding) {
          top = rect.top + scrollY - menuRect.height;
        } else if (window.innerHeight - rect.bottom < menuRect.height + viewportPadding) {
          top = window.innerHeight - menuRect.height - viewportPadding + scrollY;
        }

        if (window.innerWidth - rect.left < menuRect.width + viewportPadding &&
          rect.right > menuRect.width + viewportPadding) {
          left = rect.right + scrollX - menuRect.width;
        } else if (window.innerWidth - rect.left < menuRect.width + viewportPadding) {
          left = window.innerWidth - menuRect.width - viewportPadding + scrollX;
        }

        left = Math.max(viewportPadding + scrollX, left);
        top = Math.max(viewportPadding + scrollY, top);

        menu.style.position = 'absolute';
        menu.style.top = `${top}px`;
        menu.style.left = `${left}px`;
        menu.style.zIndex = 9999;

        // Reset temp styles so menu is visible
        menu.style.visibility = '';
        menu.style.display = '';
        menu.style.pointerEvents = '';
        menu.style.opacity = '';
      }
      // --------- END PORTAL HELPERS ---------
    },

    /** Nested Tabs */
    initTabs() {
      document.querySelectorAll('.tab[data-tab-group]').forEach(tab => {
        const groupName = tab.getAttribute('data-tab-group');
        const buttons = tab.querySelectorAll('[data-tab]');
        const contentGroups = document.querySelectorAll(`.tab-group[data-tab-group="${groupName}"]`);

        buttons.forEach(button => {
          button.addEventListener('click', () => {
            const target = button.getAttribute('data-tab');
            UIControls.utils.toggleActive(button, buttons, 'data-tab', target);

            if (target === 'all') {
              contentGroups.forEach(c => c.classList.add('is-active'));
            } else {
              contentGroups.forEach(c => {
                c.classList.toggle(
                  'is-active',
                  c.getAttribute('data-tab-content') === target
                );
              });
            }
          });
        });

        // Honor default active button on load
        const defaultBtn = tab.querySelector('[data-tab].is-active');
        if (defaultBtn) defaultBtn.click();
      });
    },

    /** Dropdowns with optional tab & multiselect */
    initDropdowns() {
      const dropdowns = document.querySelectorAll('.dropdown');

      // --------- OUTSIDE CLICK HANDLER ---------
      document.addEventListener('click', e => {
        // 1. Normal dropdown
        if (e.target.closest('.dropdown')) return;
        // 2. Portaled menu (anywhere in body)
        if (e.target.closest('.dropdown__menu--portal')) return;
        // 3. Outside: close all
        dropdowns.forEach(d => d.classList.remove('is-active'));
        // 4. Restore all portaled menus in document
        document.querySelectorAll('.dropdown__menu--portal').forEach(menu => {
          if (typeof UIControls?.utils?.restoreDropdownMenu === 'function') {
            UIControls.utils.restoreDropdownMenu(menu);
          }
        });
      });

      // --------- ESCAPE KEY HANDLER ---------
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          dropdowns.forEach(d => d.classList.remove('is-active'));
          document.querySelectorAll('.dropdown__menu--portal').forEach(menu => {
            if (typeof UIControls?.utils?.restoreDropdownMenu === 'function') {
              UIControls.utils.restoreDropdownMenu(menu);
            }
          });
        }
      });

      dropdowns.forEach(dropdown => {
        const toggle = dropdown.querySelector('.dropdown__toggle');
        const menu = dropdown.querySelector('.dropdown__menu');
        const label = dropdown.querySelector('.dropdown__label');
        const items = dropdown.querySelectorAll('.dropdown__item');
        const clearBtn = dropdown.querySelector('.dropdown__clear-all');
        const isMulti = dropdown.hasAttribute('data-multi-select');
        const isPortal = dropdown.hasAttribute('data-portal');

        dropdown.dataset.placeholder = label?.textContent?.trim() || 'Select';

        // Tabs inside dropdown
        const tab = dropdown.querySelector('.tab[data-action-tabs]');
        if (tab) {
          const tabButtons = tab.querySelectorAll('[data-action]');
          const contentBlocks = dropdown.querySelectorAll('[data-selectable-content]');

          tabButtons.forEach(button => {
            button.addEventListener('click', () => {
              tabButtons.forEach(btn => btn.classList.remove('is-active'));
              button.classList.add('is-active');

              const action = button.dataset.action;
              const target = button.dataset.target;

              if (action === 'btn') {
                UIControls.utils.clearSelections(dropdown);
                contentBlocks.forEach(c => (c.style.display = 'none'));
                dropdown.classList.remove('is-active');
                if (isPortal && menu) UIControls.utils.restoreDropdownMenu(menu);
              } else if (action === 'select') {
                contentBlocks.forEach(c => {
                  c.style.display = c.dataset.selectableContent === target ? 'block' : 'none';
                });
                UIControls.utils.updateDropdownDirection(dropdown);
              }
            });
          });
        }

        if (toggle && menu) {
          toggle.addEventListener('click', e => {
            e.stopPropagation();
            const alreadyActive = dropdown.classList.contains('is-active');

            // Close other dropdowns
            dropdowns.forEach(d => {
              if (d !== dropdown) {
                d.classList.remove('is-active');
              }
            });

            // Restore all portaled menus (except this one)
            document.querySelectorAll('.dropdown__menu--portal').forEach(portalMenu => {
              if (portalMenu !== menu && typeof UIControls?.utils?.restoreDropdownMenu === 'function') {
                UIControls.utils.restoreDropdownMenu(portalMenu);
              }
            });

            // Closing dropdown
            if (alreadyActive) {
              dropdown.classList.remove('is-active');
              if (isPortal && menu) UIControls.utils.restoreDropdownMenu(menu);
              return;
            }

            // --- PORTAL LOGIC (if enabled) ---
            if (isPortal && menu) {
              UIControls.utils.portalDropdownMenu(dropdown, menu);
              UIControls.utils.positionDropdownMenu(toggle, menu);

              // Keep menu positioned on scroll/resize while open
              const reposition = () => {
                if (dropdown.classList.contains('is-active')) {
                  UIControls.utils.positionDropdownMenu(toggle, menu);
                } else {
                  window.removeEventListener('scroll', reposition, true);
                  window.removeEventListener('resize', reposition);
                }
              };
              window.addEventListener('scroll', reposition, true);
              window.addEventListener('resize', reposition);

              // Restore on dropdown close (single source of truth)
              const closePortal = () => {
                UIControls.utils.restoreDropdownMenu(menu);
                window.removeEventListener('scroll', reposition, true);
                window.removeEventListener('resize', reposition);
              };
              dropdown.addEventListener('dropdown:close', closePortal, { once: true });
            }
            // --- END PORTAL LOGIC ---

            // --- If not portal, use old approach for direction (no changes) ---
            if (!isPortal) {
              menu.style.setProperty('position', 'absolute');
              menu.style.setProperty('visibility', 'hidden');
              menu.style.setProperty('display', 'block');

              requestAnimationFrame(() => {
                UIControls.utils.updateDropdownDirection(dropdown);

                menu.style.removeProperty('position');
                menu.style.removeProperty('visibility');
                menu.style.removeProperty('display');

                requestAnimationFrame(() => {
                  dropdown.classList.add('is-active');
                });

                // Debounced scroll/resize direction updater
                let directionTimeout;
                const updateDirection = () => {
                  if (directionTimeout) cancelAnimationFrame(directionTimeout);
                  directionTimeout = requestAnimationFrame(() => {
                    if (dropdown.classList.contains('is-active')) {
                      UIControls.utils.updateDropdownDirection(dropdown);
                    } else {
                      window.removeEventListener('scroll', updateDirection, true);
                      window.removeEventListener('resize', updateDirection);
                    }
                  });
                };

                window.addEventListener('scroll', updateDirection, true);
                window.addEventListener('resize', updateDirection);
              });
            } else {
              // For portal, just add 'is-active' immediately
              requestAnimationFrame(() => {
                dropdown.classList.add('is-active');
              });
            }
          });
        }

        // Close logic: always dispatch close event so portal cleans up
        const closeDropdown = () => {
          dropdown.classList.remove('is-active');
          if (isPortal && menu) {
            dropdown.dispatchEvent(new Event('dropdown:close'));
          }
        };

        // Clear all selected items/ tags
        if (clearBtn) {
          clearBtn.addEventListener('click', e => {
            e.stopPropagation();
            UIControls.utils.clearSelections(dropdown);

            const btnTab = dropdown.querySelector('[data-action="btn"]');
            if (btnTab) btnTab.click();
            closeDropdown();
          });
        }

        // Item Selection Function
        items.forEach(item => {
          if (item.tagName === 'INPUT') return;

          item.addEventListener('click', () => {
            const selectedMap = UIControls.utils.getSelectedMap(dropdown);
            const group = item.closest('[data-selectable-content]');
            const groupId = group?.dataset.selectableContent || 'default';
            const text = item.textContent.trim();

            if (!isMulti) {
              selectedMap.clear();
              items.forEach(i => i.classList.remove('dropdown__item--active'));
              item.classList.add('dropdown__item--active');
              label.textContent = text;
              closeDropdown();
            } else {
              if (!selectedMap.has(groupId)) selectedMap.set(groupId, new Set());
              const set = selectedMap.get(groupId);

              if (set.has(text)) {
                set.delete(text);
                item.classList.remove('dropdown__item--active');
              } else {
                set.add(text);
                item.classList.add('dropdown__item--active');
              }

              const renderTags = () => {
                label.innerHTML = '';
                selectedMap.forEach((vals, groupId) => {
                  vals.forEach(val => {
                    const tag = document.createElement('div');
                    tag.className = 'selection-tag';
                    tag.innerHTML = `${val}<i class="icon icon-xmark"></i>`;
                    tag.querySelector('.icon').addEventListener('click', e => {
                      e.stopPropagation();
                      vals.delete(val);
                      items.forEach(i => {
                        if (i.textContent.trim() === val) {
                          i.classList.remove('dropdown__item--active');
                        }
                      });
                      if (vals.size === 0) {
                        selectedMap.delete(groupId);
                      }
                      renderTags();
                    });
                    label.appendChild(tag);
                  });
                });

                dropdown.classList.toggle('has-selections', selectedMap.size > 0);

                if (selectedMap.size === 0) {
                  const btnTab = dropdown.querySelector('[data-action="btn"]');
                  if (btnTab) btnTab.click();
                }
              };

              renderTags();

              let isEmpty = true;
              selectedMap.forEach(set => {
                if (set.size > 0) isEmpty = false;
              });
              if (isEmpty) {
                selectedMap.clear();
                const btnTab = dropdown.querySelector('[data-action="btn"]');
                if (btnTab) btnTab.click();
              }
            }
          });
        });

        // Search inside dropdown Function
        const search = dropdown.querySelector('.dropdown__item-search input');
        if (search) {
          search.addEventListener('input', e => {
            const value = e.target.value.toLowerCase();
            items.forEach(item => {
              if (item.tagName === 'INPUT') return;
              item.style.display = item.textContent.toLowerCase().includes(value) ? '' : 'none';
            });
          });
        }
      });
    }
  };

  document.addEventListener('DOMContentLoaded', () => UIControls.initAll());
})();
/*----------------------------------
    Dynamic Close with Button, Outside, ESC Press
-----------------------------------*/
function setupModalSystem() {
  document.addEventListener("click", (e) => {
    const overlay = document.querySelector(".overlay");

    // 1. Open modal via trigger
    const trigger = e.target.closest("[data-modal-trigger]");
    if (trigger) {
      const selector = trigger.getAttribute("data-modal-trigger");
      const modal = document.querySelector(selector);
      if (modal) {
        modal.classList.remove("is-closed");
        modal.classList.add("is-open");
        document.body.classList.add("no-scroll");
        if (modal.hasAttribute("data-overlay") && overlay) {
          overlay.classList.add("is-open");
          overlay.setAttribute("data-target", selector); // Track which modal it relates to
        }
      }
      return;
    }

    // 2. Close via [data-close] button
    const closeBtn = e.target.closest("[data-close]");
    if (closeBtn) {
      const modal = closeBtn.closest("[data-close-target]");
      if (modal) closeModal(modal, overlay);
      return;
    }

    // 3. Close via overlay click
    if (e.target.classList.contains("overlay")) {
      const modalSelector = overlay?.getAttribute("data-target");
      const modal = modalSelector ? document.querySelector(modalSelector) : null;
      if (modal) closeModal(modal, overlay);
      overlay?.removeAttribute("data-target"); // Cleanup
    }
  });

  // 4. Close via Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" || e.key === "Esc") {
      const modal = document.querySelector("[data-close-target].is-open[data-overlay]");
      const overlay = document.querySelector(".overlay");
      if (modal) closeModal(modal, overlay);
      overlay?.removeAttribute("data-target");
    }
  });
}

function closeModal(modal, overlay) {
  modal.classList.remove("is-open");
  modal.classList.add("is-closed");
  document.body.classList.remove("no-scroll");
  if (modal.hasAttribute("data-overlay") && overlay) {
    overlay.classList.remove("is-open");
  }
}

// Initialize
setupModalSystem();


/*----------------------------------
    Reset Controlbar State (Reset button on sidebar)
-----------------------------------*/
(() => {
  const ControlResetPlugin = {
    init() {
      const form = document.querySelector(".sidebar--control__body");
      const resetBtn = document.querySelector(".sidebar--control__reset");
      if (!form || !resetBtn) return;

      const showBtn = () => (resetBtn.style.display = "flex");

      // Show reset button on input
      form.addEventListener("input", e => {
        if (e.target.matches("input:not([type='checkbox']):not([type='radio']), textarea")) showBtn();
      });

      // Show reset button on dropdown interaction (excluding tabs/generate)
      form.addEventListener("click", e => {
        const el = e.target;
        const isDropdown = el.closest(".dropdown__item, .dropdown__toggle");
        const isIgnored = el.closest(".tab, .button--generate");
        if (isDropdown && !isIgnored) showBtn();
      });

      // Reset all states
      resetBtn.addEventListener("click", () => {
        document.querySelectorAll(".dropdown").forEach(drop => {
          drop.classList.remove("has-selections", "dropdown--up");
          drop.querySelectorAll(".dropdown__item--active, .is-active").forEach(el => el.classList.remove("dropdown__item--active", "is-active"));
          drop.querySelectorAll("input[type='text']").forEach(i => (i.value = ""));
          const label = drop.querySelector(".dropdown__label");
          if (label) label.textContent = drop.dataset.placeholder || "Select";
        });

        form.querySelectorAll("input:not([type='checkbox']), textarea").forEach(i => (i.value = ""));
        resetBtn.removeAttribute("style");
      });
    }
  };

  document.addEventListener("DOMContentLoaded", ControlResetPlugin.init);
  window.ControlResetPlugin = ControlResetPlugin;
})();
/*----------------------------------
    Before After Slider
-----------------------------------*/
class BeforeAfterSlider {
  constructor(container, options = {}) {
    if (!container) return;

    this.container = container;
    this.afterImage = container.querySelector('.after');
    this.handle = container.querySelector('.handle');
    if (!this.afterImage || !this.handle) return;

    // Options
    function getOption(dataset, options, key, fallback, parse = val => val) {
      if (dataset.hasOwnProperty(key)) {
        return parse(dataset[key]);
      }
      if (options.hasOwnProperty(key)) {
        return options[key];
      }
      return fallback;
    }
    const dataset = container.dataset;

    this.mode = getOption(dataset, options, 'mode', 'hover');
    this.autoReset = getOption(dataset, options, 'autoReset', false, v => v === 'true');
    this.dragAnywhere = getOption(dataset, options, 'dragAnywhere', false, v => v === 'true');
    this.defaultPercent = getOption(dataset, options, 'defaultPercent', 100, parseFloat);
    this.activePercent = getOption(dataset, options, 'activePercent', 50, parseFloat);
    this.lightboxEnabled = getOption(dataset, options, 'lightbox', false, v => v === 'true');

    // External toggle
    this.switch = document.querySelector(`.compare-switch[data-target="#${container.id}"]`);
    this.isActive = this.switch ? false : true;

    // Internal state
    this.currentPercent = this.defaultPercent;
    this.targetPercent = this.defaultPercent;
    this.animationFrame = null;
    this.isDragging = false;

    this.init();
  }

  init() {
    this.container.classList.add('inactive');
    this.afterImage.style.clipPath = `inset(0 ${100 - this.currentPercent}% 0 0)`;
    this.handle.style.left = `${this.currentPercent}%`;

    this.animate = this.animate.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);

    if (this.mode === 'drag' || this.mode === 'both') {
      this.container.classList.add('drag-enabled');
      if (this.dragAnywhere) {
        this.container.classList.add('drag-anywhere');
      }
    }

    if (this.mode === 'hover' || this.mode === 'both') {
      this.container.addEventListener('mousemove', this.onMove);
      this.container.addEventListener('touchmove', e => {
        if (e.touches.length === 1) this.onMove(e.touches[0]);
      });
    }

    if (this.mode === 'drag' || this.mode === 'both') {
      const dragTarget = this.dragAnywhere ? this.container : this.handle;
      dragTarget.addEventListener('mousedown', this.onDragStart);
      window.addEventListener('mouseup', this.onDragEnd);
      window.addEventListener('mousemove', this.onMove);

      dragTarget.addEventListener('touchstart', this.onDragStart);
      window.addEventListener('touchend', this.onDragEnd);
      window.addEventListener('touchmove', e => {
        if (e.touches.length === 1 && this.isDragging) this.onMove(e.touches[0]);
      });
    }

    // Optional switch toggle
    if (this.switch) {
      // Set initial state based on checked value
      this.isActive = this.switch.checked;
      this.targetPercent = this.isActive ? this.activePercent : this.defaultPercent;
      this.container.classList.toggle('inactive', !this.isActive);
      this.animationFrame = requestAnimationFrame(() => {
        this.updateCursor();
        this.animate();
      });

      this.switch.addEventListener('change', () => {
        this.isActive = this.switch.checked;
        this.targetPercent = this.isActive ? this.activePercent : this.defaultPercent;
        this.container.classList.toggle('inactive', !this.isActive);
        this.updateCursor();
        if (!this.animationFrame) this.animationFrame = requestAnimationFrame(this.animate);
      });
    } else {
      this.isActive = true;
      this.targetPercent = this.activePercent;
      this.container.classList.remove('inactive');
      this.animationFrame = requestAnimationFrame(() => {
        this.updateCursor();
        this.animate();
      });
    }
    // Optional lightbox support
    if (this.lightboxEnabled && this.afterImage) {
      this.afterImage.style.cursor = 'zoom-in';
      this.afterImage.addEventListener('click', () => {
        if (!this.isActive) {
          new DecorLightboxGallery(this.afterImage, '.thumbnails', {
            loop: true
          });
        }
      });
    }
  }

  updateCursor() {
    if (!this.isActive) {
      this.container.style.cursor = 'default';
      if (this.afterImage) {
        this.afterImage.style.cursor = this.lightboxEnabled ? 'zoom-in' : 'default';
      }
    } else {
      if (this.mode === 'drag' && this.dragAnywhere) {
        this.container.style.cursor = 'ew-resize';
      } else {
        this.container.style.cursor = 'default';
      }

      if (this.afterImage) {
        this.afterImage.style.cursor = 'ew-resize';
      }
    }
  }

  getOffsetPercent(x) {
    const rect = this.container.getBoundingClientRect();
    let offset = x - rect.left;
    offset = Math.max(0, Math.min(offset, rect.width));
    return (offset / rect.width) * 100;
  }

  onMove(e) {
    if (!this.isActive) return;
    const x = e.clientX || e.pageX;
    if (this.isDragging) {
      this.currentPercent = this.getOffsetPercent(x);
      this.afterImage.style.clipPath = `inset(0 ${100 - this.currentPercent}% 0 0)`;
      this.handle.style.left = `${this.currentPercent}%`;
      return;
    }
    if ((this.mode === 'drag' || this.mode === 'both') && !this.isDragging && this.dragAnywhere) return;
    this.targetPercent = this.getOffsetPercent(x);
    if (!this.animationFrame) this.animationFrame = requestAnimationFrame(this.animate);
  }

  onDragStart(e) {
    if (!this.isActive) return;
    this.isDragging = true;
    e.preventDefault();
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;

    if (this.autoReset && this.isActive) {
      this.targetPercent = this.activePercent;
      this.animationFrame = requestAnimationFrame(this.animate);
    }
  }

  animate() {
    const diff = this.targetPercent - this.currentPercent;
    if (Math.abs(diff) > 0.5) {
      this.currentPercent += diff * 0.15;
      this.afterImage.style.clipPath = `inset(0 ${100 - this.currentPercent}% 0 0)`;
      this.handle.style.left = `${this.currentPercent}%`;
      this.animationFrame = requestAnimationFrame(this.animate);
    } else {
      this.currentPercent = this.targetPercent;
      this.afterImage.style.clipPath = `inset(0 ${100 - this.currentPercent}% 0 0)`;
      this.handle.style.left = `${this.currentPercent}%`;
      this.animationFrame = null;
    }
  }

  openLightbox(src) {
    const overlay = document.createElement('div');
    overlay.className = 'decor-lightbox';
    overlay.innerHTML = `
    <div class="decor-lightbox-content">
      <img src="${src}" alt="Zoomed Preview" />
      <button class="decor-lightbox-close" aria-label="Close">&times;</button>
    </div>
  `;

    document.body.appendChild(overlay);

    overlay.querySelector('.decor-lightbox-close').addEventListener('click', () => {
      overlay.remove();
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });

    // Optional: close with ESC
    const escListener = (e) => {
      if (e.key === 'Escape') {
        overlay.remove();
        document.removeEventListener('keydown', escListener);
      }
    };
    document.addEventListener('keydown', escListener);
  }
}
// Init
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.js-before-after');
  if (container) {
    new BeforeAfterSlider(container, {
      mode: 'drag',    // drag or 'hover' or 'both'
      dragAnywhere: true,   // allow dragging from entire image
      autoReset: false,    // resets after release
      defaultPercent: 105,    // starting state (full after image)
      activePercent: 50,    // reveal amount on activation
      lightbox: true   // enable lightbox when compare is off
    });
  }
});

/*----------------------------------
    Carousel Slider
-----------------------------------*/
class ThumbnailCarouselSync {
  constructor(wrapperSelector = '.decor-before-after', thumbnailsSelector = '.thumbnails') {
    this.wrappers = document.querySelectorAll(wrapperSelector);
    this.thumbnails = document.querySelectorAll(`${thumbnailsSelector} .thumbnail`);

    if (!this.wrappers.length || !this.thumbnails.length) return;

    this.init();
  }

  init() {
    this.thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const targetSrc = thumb.getAttribute('data-after');
        if (!targetSrc) return;

        // Remove active class from all thumbnails in the same group
        const parent = thumb.closest('.thumbnails');
        parent.querySelectorAll('.thumbnail').forEach(t => t.classList.remove('is-active'));

        // Add active class to the clicked thumbnail
        thumb.classList.add('is-active');

        // Update each active slider (you could scope to one if needed)
        this.wrappers.forEach(wrapper => {
          const container = wrapper.querySelector('.before-after-container');
          const afterImg = container?.querySelector('.after');

          if (afterImg) {
            this.fadeReplaceImage(afterImg, targetSrc);
          }
        });
      });
    });
  }

  fadeReplaceImage(imgEl, newSrc) {
    const clone = imgEl.cloneNode(true);
    clone.src = newSrc;

    Object.assign(clone.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      zIndex: '4',
      opacity: '0',
      filter: 'blur(2.5px)',
      transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), filter 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      pointerEvents: 'none',
    });

    imgEl.parentNode.style.position = 'relative';
    imgEl.parentNode.appendChild(clone);

    requestAnimationFrame(() => {
      // Step 1: transition in
      clone.style.opacity = '1';
      clone.style.filter = 'blur(0px)';
      imgEl.style.opacity = '0.2'; // keep a touch of base image for depth
    });

    setTimeout(() => {
      // Step 2: swap base + cleanup
      imgEl.src = newSrc;
      imgEl.style.opacity = '1';
      clone.remove();
    }, 600); // match transition time
  }
}
// Init
document.addEventListener('DOMContentLoaded', () => {
  new ThumbnailCarouselSync('.decor-before-after', '.thumbnails');
});

/*----------------------------------
    Lightbox
-----------------------------------*/
class DecorLightboxGallery {
  constructor(triggerImage, options = {}) {
    if (!triggerImage) return;
    if (document.querySelector('.decor-lightbox')) return; // prevent multiple

    // Defaults
    const config = {
      thumbnailSelector: '.thumbnail',
      thumbnailContainer: '.thumbnails',
      loop: true,
      onOpen: null,
      onClose: null,
      ...options
    };

    this.config = config;
    this.triggerImage = triggerImage;

    this.galleryContainer = document.querySelector(config.thumbnailContainer);
    if (!this.galleryContainer) return;

    this.images = Array.from(this.galleryContainer.querySelectorAll(config.thumbnailSelector))
      .map(img => img.getAttribute('data-after'))
      .filter(Boolean);

    const activeThumb = this.galleryContainer.querySelector('.thumbnail.is-active');
    const activeSrc = activeThumb?.getAttribute('data-after');

    if (activeSrc && this.images.includes(activeSrc)) {
      this.currentIndex = this.images.indexOf(activeSrc);
    } else {
      this.currentIndex = this.images.indexOf(triggerImage.src);
      if (this.currentIndex === -1) this.currentIndex = 0;
    }
    this.loop = config.loop;
    this.onKeyDown = this.onKeyDown.bind(this);

    this.open();
  }

  open() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'decor-lightbox scale-in';

    const thumbnailsHTML = this.images
      .map((src, i) => `<img src="${src}" data-index="${i}" class="decor-lightbox-thumb${i === this.currentIndex ? ' active' : ''}">`)
      .join('');

    this.overlay.innerHTML = `
      <div class="decor-lightbox-content">
        <img src="${this.images[this.currentIndex]}" class="decor-lightbox-main" alt="Zoomed View" />
        <button class="decor-lightbox-close" aria-label="Close"><i class="icon icon-xmark"></i></button>
        <button class="decor-lightbox-prev" aria-label="Previous"><i class="icon icon-left-arrow"></i></button>
        <button class="decor-lightbox-next" aria-label="Next"><i class="icon icon-right-arrow"></i></button>
        <div class="decor-lightbox-thumbnails">${thumbnailsHTML}</div>
      </div>
    `;

    document.body.appendChild(this.overlay);

    this.mainImage = this.overlay.querySelector('.decor-lightbox-main');
    this.mainImage.addEventListener('click', () => this.close());
    this.thumbEls = this.overlay.querySelectorAll('.decor-lightbox-thumb');

    requestAnimationFrame(() => {
      this.overlay.classList.add('visible');
      this.overlay.querySelector('.decor-lightbox-content')?.classList.add('scaled-in');
    });

    // Event handlers
    this.overlay.querySelector('.decor-lightbox-close').addEventListener('click', () => this.close());
    this.overlay.querySelector('.decor-lightbox-next').addEventListener('click', () => this.nextImage());
    this.overlay.querySelector('.decor-lightbox-prev').addEventListener('click', () => this.prevImage());
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    this.thumbEls.forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.currentIndex = parseInt(thumb.dataset.index, 10);
        this.updateImage();
      });
    });

    document.addEventListener('keydown', this.onKeyDown);

    // Callback
    if (typeof this.config.onOpen === 'function') {
      this.config.onOpen(this);
    }
  }

  updateImage() {
    this.mainImage.src = this.images[this.currentIndex];
    this.thumbEls.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === this.currentIndex);
    });
  }

  nextImage() {
    if (this.currentIndex < this.images.length - 1) {
      this.currentIndex++;
    } else if (this.loop) {
      this.currentIndex = 0;
    }
    this.updateImage();
  }

  prevImage() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else if (this.loop) {
      this.currentIndex = this.images.length - 1;
    }
    this.updateImage();
  }

  close() {
    this.overlay.classList.remove('scale-in');
    this.overlay.classList.add('scale-out');

    setTimeout(() => {
      this.overlay.remove();
      if (typeof this.config.onClose === 'function') {
        this.config.onClose(this);
      }
    }, 300);

    document.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown(e) {
    if (e.key === 'Escape') this.close();
    if (e.key === 'ArrowRight') this.nextImage();
    if (e.key === 'ArrowLeft') this.prevImage();
  }
}
/*----------------------------------
    Video Player
-----------------------------------*/
/*! DecorMiniPlayer v0.3 — minimal, control-bar only */
(function (global) {
  const bool = (v, d = false) => v == null ? d : String(v).toLowerCase() === "true";
  const fmt = s => {
    if (!Number.isFinite(s)) return "0:00";
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60).toString().padStart(2, "0");
    return h ? `${h}:${m.toString().padStart(2, "0")}:${sec}` : `${m}:${sec}`;
  };

  class DecorMiniPlayer {
    constructor(root, opts = {}) {
      if (!root) return;
      this.root = root;
      this.v = root.querySelector(".decor-player__video");
      this.btnPlay = root.querySelector(".dp-play");
      this.btnFull = root.querySelector(".dp-full");
      this.btnMute = root.querySelector(".dp-mute");
      this.seek = root.querySelector(".dp-seek");
      this.fill = root.querySelector(".dp-fill");
      this.cur = root.querySelector(".dp-current");
      this.dur = root.querySelector(".dp-duration");
      this.vol = root.querySelector(".dp-volume");

      if (!this.v || !this.btnPlay || !this.seek || !this.fill) return; // hard guard

      const ds = root.dataset || {};
      this.opts = Object.assign({
        startMuted: true,
        hideVolume: bool(ds.hideVolume, false),
        loop: bool(ds.loop, false),
        objectFitFS: "contain"
      }, opts);

      // source (internal/external)
      const dataSrc = root.getAttribute("data-src");
      if (dataSrc) this.v.src = dataSrc;

      // base attrs
      this.v.setAttribute("playsinline", "");
      if (this.opts.startMuted) { this.v.muted = true; this.root.classList.add("is-muted"); }
      this.v.loop = !!this.opts.loop;

      // hide volume UI if asked
      if (this.opts.hideVolume) { this.btnMute?.style && (this.btnMute.style.display = "none"); this.vol?.style && (this.vol.style.display = "none"); }

      this._rAF = 0; this._dragging = false;
      this._bind();
    }

    static initAll(sel = ".decor-player", opts = {}) {
      const nodes = document.querySelectorAll(sel);
      if (!nodes.length) return [];
      return Array.from(nodes).map(n => new DecorMiniPlayer(n, opts));
    }

    _bind() {
      const v = this.v;

      // play/pause
      const toggle = () => v.paused ? v.play().catch(() => { }) : v.pause();
      this.btnPlay.addEventListener("click", toggle);
      v.addEventListener("click", toggle);

      // double-click fullscreen (container)
      this.root.addEventListener("dblclick", () => this._toggleFS());

      // state UI
      v.addEventListener("play", () => { this.root.classList.add("is-playing"); this._startRAF(); this.btnPlay.textContent = "❚❚"; });
      v.addEventListener("pause", () => { this.root.classList.remove("is-playing"); this._stopRAF(); this.btnPlay.textContent = "▶︎"; });
      v.addEventListener("loadedmetadata", () => { if (this.dur) this.dur.textContent = fmt(v.duration); this._update(); });

      // seek (smooth with rAF; suspend while dragging)
      const hold = () => this._dragging = true;
      const release = () => { this._dragging = false; this._update(); };
      this.seek.addEventListener("mousedown", hold);
      this.seek.addEventListener("touchstart", hold, { passive: true });
      this.seek.addEventListener("mouseup", release);
      this.seek.addEventListener("touchend", release);
      this.seek.addEventListener("input", () => {
        const pct = parseFloat(this.seek.value) || 0;
        v.currentTime = (pct / 100) * (v.duration || 0);
        this.fill.style.width = `${pct}%`;
        if (!this._rAF) this._update();
      });

      // mute / volume
      this.btnMute?.addEventListener("click", () => {
        v.muted = !v.muted; this.root.classList.toggle("is-muted", v.muted);
      });
      this.vol?.addEventListener("input", () => {
        const x = parseFloat(this.vol.value); v.volume = isNaN(x) ? 1 : x; v.muted = v.volume === 0; this.root.classList.toggle("is-muted", v.muted);
      });

      // fullscreen button
      this.btnFull?.addEventListener("click", () => this._toggleFS());

      // fallback progress updates
      v.addEventListener("timeupdate", () => { if (this._dragging) return; this._update(); });

      // errors: keep it silent/minimal
      v.addEventListener("error", () => {/* optional tiny toast here */ });
    }

    _startRAF() {
      if (this._rAF) return;
      const step = () => {
        this._rAF = requestAnimationFrame(step);
        if (!this._dragging) this._update();
      };
      this._rAF = requestAnimationFrame(step);
    }
    _stopRAF() { if (!this._rAF) return; cancelAnimationFrame(this._rAF); this._rAF = 0; }

    _update() {
      const v = this.v;
      const d = v.duration || 0, t = v.currentTime || 0;
      const pct = d ? (t / d) * 100 : 0;
      this.seek.value = pct || 0;
      this.fill.style.width = `${pct || 0}%`;
      if (this.cur) this.cur.textContent = fmt(t);
      if (this.dur && Number.isFinite(d)) this.dur.textContent = fmt(d);
    }

    _toggleFS() {
      const ios = this.v.webkitSupportsFullscreen && typeof this.v.webkitEnterFullscreen === "function";
      if (ios) { if (document.fullscreenElement) document.exitFullscreen?.(); else this.v.webkitEnterFullscreen(); return; }
      if (!document.fullscreenElement) this.root.requestFullscreen?.(); else document.exitFullscreen?.();
      // style tweaks managed by CSS in your project if needed
      this.v.style.objectFit = document.fullscreenElement ? "contain" : "";
      this.v.style.maxHeight = document.fullscreenElement ? "none" : "";
    }
  }

  // expose + safe auto-init
  global.DecorMiniPlayer = DecorMiniPlayer;
  const boot = () => DecorMiniPlayer.initAll(".decor-player");
  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", boot) : boot();
})(window);
/*----------------------------------
    Toggle
-----------------------------------*/
// Target Toggle and Fire
function initTargetToggle(globalOptions = {}) {
  const fallback = {
    prefix: "fire-",
    activeClass: "is-open",
    scrollLockClass: null
  };

  const allButtons = document.querySelectorAll("[data-attr]");

  allButtons.forEach(button => {
    const dataset = button.dataset;

    // Options with priority: HTML > init > fallback
    const prefix = dataset.prefix || globalOptions.prefix || fallback.prefix;
    const activeClass = dataset.activeClass || globalOptions.activeClass || fallback.activeClass;
    const scrollLockClass =
      dataset.scrollLockClass !== undefined && dataset.scrollLockClass !== ""
        ? dataset.scrollLockClass
        : globalOptions.scrollLockClass || fallback.scrollLockClass;

    const attrValue = dataset.attr;
    if (!attrValue || !attrValue.startsWith(prefix)) return;

    const targetAttr = attrValue.replace(prefix, "");
    const target = document.querySelector(`[data-attr="${targetAttr}"]`);
    if (!target) return;

    // Extended options with JS fallback
    const closeOthers = dataset.closeOthers !== undefined
      ? true
      : globalOptions.closeOthers || false;

    const closeOnOutside = dataset.closeOutside !== undefined
      ? true
      : globalOptions.closeOutside || false;

    const closeOnEsc = dataset.closeOnEsc !== undefined
      ? true
      : globalOptions.closeOnEsc || false;
    button.addEventListener("click", () => {
      const isNowOpen = !target.classList.contains(activeClass);

      // Close others
      if (closeOthers && isNowOpen) {
        document.querySelectorAll(`[data-attr].${activeClass}`).forEach(el => {
          if (el !== target) el.classList.remove(activeClass);
        });
      }

      target.classList.toggle(activeClass, isNowOpen);

      // Scroll lock body class
      if (scrollLockClass) {
        const anyOpen = document.querySelector(`[data-attr].${activeClass}`);
        document.body.classList.toggle(scrollLockClass, !!anyOpen);
      }

      // Close on outside click
      if (closeOnOutside && isNowOpen) {
        setTimeout(() => {
          const outsideHandler = e => {
            if (!target.contains(e.target) && !button.contains(e.target)) {
              target.classList.remove(activeClass);
              document.removeEventListener("click", outsideHandler);
              if (scrollLockClass) document.body.classList.remove(scrollLockClass);
            }
          };
          document.addEventListener("click", outsideHandler);
        }, 10);
      }

      // Close on ESC
      if (closeOnEsc && isNowOpen) {
        const escHandler = e => {
          if (e.key === "Escape") {
            target.classList.remove(activeClass);
            document.removeEventListener("keydown", escHandler);
            if (scrollLockClass) document.body.classList.remove(scrollLockClass);
          }
        };
        document.addEventListener("keydown", escHandler);
      }
    });
  });
}
// Init
initTargetToggle({
  prefix: "fire-",
  activeClass: "is-open",
  // scrollLockClass: "no-scroll",
  closeOnEsc: true,
  closeOnClickOutside: false,
  closeOthers: true
});
// Self (This) Element Toggle
function initToggleSelf(className = 'is-active') {
  const selector = '[data-toggle-self]';
  document.addEventListener('click', e => {
    const btn = e.target.closest(`${selector}:not(li${selector})`);
    const li = e.target.closest(`li${selector}[data-toggle-close-outside]`);
    // Skip if click is inside tooltip-control
    if (e.target.closest('.tooltip-control')) return;
    // Toggle button (and its parent <li> if data-toggle-close-outside)
    if (btn) {
      const single = btn.hasAttribute('data-toggle-single');
      const active = btn.classList.contains(className);

      if (single) {
        document.querySelectorAll(`${selector}[data-toggle-single].${className}`).forEach(el => {
          if (el !== btn) el.classList.remove(className);
          const parent = el.closest(`li${selector}[data-toggle-close-outside]`);
          if (parent) parent.classList.remove(className);
        });
      }
      btn.classList.toggle(className, !active);
      const parent = btn.closest(`li${selector}[data-toggle-close-outside]`);
      if (parent) parent.classList.toggle(className, !active);
      return;
    }
    // Toggle class on <li> directly if not clicking a button
    if (li && !e.target.closest('button')) {
      li.classList.toggle(className);
      return;
    }
    // Remove active from all close-outside toggles
    document.querySelectorAll(`${selector}[data-toggle-close-outside].${className}`).forEach(el => {
      el.classList.remove(className);
    });
  });
}

initToggleSelf(); // optionally pass 'active' or another class
/*----------------------------------
    Range Slider
-----------------------------------*/
document.querySelectorAll('.decor-slider').forEach(slider => {
  slider.addEventListener('input', () => {
    const percent = (slider.value - slider.min) / (slider.max - slider.min) * 100;
    slider.style.background = `linear-gradient(to right, var(--color-primary) ${percent}%, var(--neutral-04) 0)`;
  });
});

/*----------------------------------
    Tooltip
-----------------------------------*/
class DecorTooltip {
  constructor(options = {}) {
    this.options = {
      offset: options.offset || 8,
      selector: options.selector || '[data-tooltip]',
      attrText: options.attrText || 'data-tooltip',
      attrPosition: options.attrPosition || 'data-tooltip-position',
      attrToggle: options.attrToggle || 'data-tooltip-toggle',
    };

    this.tooltip = document.createElement('div');
    this.tooltip.className = 'tooltip';
    document.body.appendChild(this.tooltip);

    this.currentEl = null;
    this.init();
  }

  init() {
    const elements = document.querySelectorAll(this.options.selector);
    elements.forEach(el => {
      this.bindTooltip(el);

      // Check for toggle support
      if (el.hasAttribute(this.options.attrToggle)) {
        this.syncToggleText(el);

        el.addEventListener('click', () => {
          requestAnimationFrame(() => {
            this.syncToggleText(el);
          });
        });
      }
    });
  }

  bindTooltip(el) {
    el.addEventListener('mouseenter', () => this.showTooltip(el));
    el.addEventListener('mouseleave', () => this.hideTooltip());
  }

  showTooltip(el) {
    this.currentEl = el;

    const text = el.getAttribute(this.options.attrText);
    const position = el.getAttribute(this.options.attrPosition) || 'top';
    const pos = position.split(' ');

    this.tooltip.textContent = text;
    this.tooltip.setAttribute('data-pos', position);
    this.tooltip.classList.add('visible');
    this.tooltip.style.visibility = 'hidden';

    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const tipRect = this.tooltip.getBoundingClientRect();
      let top = 0, left = 0;

      // Vertical positioning
      if (pos.includes('top')) {
        top = rect.top - tipRect.height - this.options.offset;
      } else if (pos.includes('bottom')) {
        top = rect.bottom + this.options.offset;
      } else {
        top = rect.top + (rect.height - tipRect.height) / 2;
      }

      // Horizontal positioning
      if (pos.includes('left')) {
        left = rect.left - tipRect.width - this.options.offset;
      } else if (pos.includes('right')) {
        left = rect.right + this.options.offset;
      } else {
        left = rect.left + (rect.width - tipRect.width) / 2;
      }

      // Viewport bounds correction
      const vw = window.innerWidth, vh = window.innerHeight;
      if (left + tipRect.width > vw) left = vw - tipRect.width - 8;
      if (left < 0) left = 8;
      if (top + tipRect.height > vh) top = vh - tipRect.height - 8;
      if (top < 0) top = 8;

      this.tooltip.style.top = `${top + window.scrollY}px`;
      this.tooltip.style.left = `${left + window.scrollX}px`;
      this.tooltip.style.visibility = 'visible';
    });
  }

  hideTooltip() {
    this.tooltip.classList.remove('visible');
    this.tooltip.removeAttribute('data-pos');
    this.tooltip.style.visibility = 'hidden';
    this.currentEl = null;
  }

  update(el, newText) {
    el.setAttribute(this.options.attrText, newText);
    if (this.currentEl === el) {
      this.tooltip.textContent = newText;
    }
  }

  syncToggleText(el) {
    const toggleTexts = el.getAttribute(this.options.attrToggle);
    if (!toggleTexts) return;

    const [defaultText, activeText] = toggleTexts.split('|');
    const isActive = el.classList.contains('is-active');
    const newText = isActive ? activeText : defaultText;

    this.update(el, newText);
  }
}

// Init
const tooltips = new DecorTooltip();

/*----------------------------------
    Global Search Plugin
    A lightweight, reusable search/filter plugin
-----------------------------------*/

class DecorSearch {
  constructor(input, target, options = {}) {
    this.input = input;

    if (typeof target === 'string') {
      this.targets = Array.from(document.querySelectorAll(target));
      this.target = this.targets[0];
    } else {
      this.target = target;
      this.targets = [target];
    }

    if (!this.targets.length || this.targets.every(t => t === null)) {
      throw new Error(`DecorSearch: No valid targets found for selector "${target}"`);
    }

    this.options = {
      caseSensitive: false,
      matchAttribute: null,
      searchDelay: 150,
      noResultsCallback: null,
      onSearch: null,
      minChars: 1,
      itemSelector: null,
      textSelector: null,
      searchInSubmenu: true,
      activeTabSelector: '.is-active',
      searchAllTabs: false,
      ...options
    };

    this.searchTimeout = null;
    this.originalItems = [];

    this.init();
  }

  init() {
    if (!this.input || !this.targets.length) {
      console.error('DecorSearch: Invalid input or target element');
      return;
    }

    this.originalItems = this.getSearchableItems();

    this.input.addEventListener('input', (e) => {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.search(e.target.value);
      }, this.options.searchDelay);
    });

    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.clear();
      }
    });
  }

  getSearchableItems() {
    let allItems = [];

    const targetsToSearch = this.options.searchAllTabs
      ? this.targets
      : this.targets.filter(target => target.classList.contains(this.options.activeTabSelector.replace('.', '')) || target.matches(this.options.activeTabSelector));

    const finalTargets = targetsToSearch.length > 0 ? targetsToSearch : this.targets;

    finalTargets.forEach(target => {
      if (this.options.itemSelector) {
        allItems.push(...Array.from(target.querySelectorAll(this.options.itemSelector)));
        return;
      }

      const directChildren = Array.from(target.children);

      if (directChildren.length === 1) {
        const firstChild = directChildren[0];
        const grandChildren = Array.from(firstChild.children);
        if (grandChildren.length > 0) {
          allItems.push(...grandChildren);
          return;
        }
      }

      allItems.push(...directChildren);
    });

    return allItems;
  }

  search(query) {
    const trimmedQuery = query.trim();

    if (this.options.onSearch) {
      this.options.onSearch(trimmedQuery, this.originalItems);
    }

    if (trimmedQuery.length < this.options.minChars) {
      this.showAll();
      return;
    }

    const searchQuery = this.options.caseSensitive ? trimmedQuery : trimmedQuery.toLowerCase();
    let visibleCount = 0;

    this.originalItems.forEach(item => {
      const searchText = this.getSearchText(item);
      const matches = this.options.caseSensitive
        ? searchText.includes(searchQuery)
        : searchText.toLowerCase().includes(searchQuery);

      if (matches) {
        item.style.display = '';
        visibleCount++;
      } else {
        item.style.display = 'none';
      }
    });

    if (visibleCount === 0 && this.options.noResultsCallback) {
      this.options.noResultsCallback(trimmedQuery);
    }
  }

  getSearchText(element) {
    if (this.options.matchAttribute) {
      return element.getAttribute(this.options.matchAttribute) || '';
    }

    if (this.options.textSelector) {
      const textElement = element.querySelector(this.options.textSelector);
      if (textElement) {
        return textElement.textContent || textElement.innerText || '';
      }
    }

    return element.textContent || element.innerText || '';
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  showAll() {
    this.originalItems.forEach(item => {
      item.style.display = '';
    });
  }

  clear() {
    this.input.value = '';
    this.showAll();
  }

  destroy() {
    clearTimeout(this.searchTimeout);
    this.input.removeEventListener('input', this.search);
    this.input.removeEventListener('keydown', this.clear);
  }

  static init(selector = '[data-search-target]', options = {}) {
    const inputs = document.querySelectorAll(selector);
    const instances = [];

    if (inputs.length === 0) {
      return instances;
    }

    inputs.forEach(input => {
      const target = input.getAttribute('data-search-target');
      if (!target) {
        console.warn('DecorSearch: Input found but no data-search-target attribute', input);
        return;
      }

      let targetSelector = target;
      if (!target.startsWith('#') && !target.startsWith('.')) {
        const classTarget = document.querySelector(`.${target}`);
        const idTarget = document.querySelector(`#${target}`);

        if (classTarget) {
          targetSelector = `.${target}`;
        } else if (idTarget) {
          targetSelector = `#${target}`;
        } else {
          console.warn(`DecorSearch: Target "${target}" not found for input`, input);
          return;
        }
      } else {
        if (!document.querySelector(targetSelector)) {
          console.warn(`DecorSearch: Target "${targetSelector}" not found for input`, input);
          return;
        }
      }

      try {
        instances.push(new DecorSearch(input, targetSelector, options));
      } catch (error) {
        console.error('DecorSearch: Error initializing instance', error, input);
      }
    });

    return instances;
  }
}

// Auto-initialize on DOM ready with error handling
document.addEventListener('DOMContentLoaded', () => {
  DecorSearch.init();
});

// Export for module systems
// if (typeof module !== 'undefined' && module.exports) {
//   module.exports = DecorSearch;
// }

/*----------------------------------
    Add class on typing to main navbar
    Usage in Nav Bar Large
-----------------------------------*/
const searchInput = document.querySelector('[data-search-target="#searchgroup"]');
const searchGroup = document.querySelector('#searchgroup');

if (searchInput && searchGroup) {
  searchInput.addEventListener('input', () => {
    if (searchInput.value.trim() !== '') {
      searchGroup.classList.add('is-open');
    }
  });
}
/*----------------------------------
    Asset Browser Toggler
-----------------------------------*/
(() => {
  const closeClassTargets = document.querySelectorAll('.asset__selector, .sidebar--control__header');
  const assetBrowser = document.querySelector('.asset__browser');

  function setIsCloseState(shouldAdd) {
    closeClassTargets.forEach(el => el.classList.toggle('is-close', shouldAdd));
  }

  const buttonUserAssets = document.querySelector('.js-browse-user-assets');
  const buttonDecorAssets = document.querySelector('.js-browse-decor-assets');

  const userAssetPanel = document.querySelector('.asset__browser--user-uploaded');
  const decorAssetPanel = document.querySelector('.asset__browser--decor');

  if (buttonUserAssets && userAssetPanel) {
    buttonUserAssets.addEventListener('click', () => {
      userAssetPanel.classList.add('is-active');
      decorAssetPanel?.classList.remove('is-active');
      assetBrowser?.classList.add('is-active');
      setIsCloseState(true);
    });
  }

  if (buttonDecorAssets && decorAssetPanel) {
    buttonDecorAssets.addEventListener('click', () => {
      decorAssetPanel.classList.add('is-active');
      userAssetPanel?.classList.remove('is-active');
      assetBrowser?.classList.add('is-active');
      setIsCloseState(true);
    });
  }

  document.querySelectorAll('.js-back-to-asset').forEach(backButton => {
    backButton.addEventListener('click', () => {
      userAssetPanel?.classList.remove('is-active');
      decorAssetPanel?.classList.remove('is-active');
      assetBrowser?.classList.remove('is-active');
      setIsCloseState(false);
    });
  });
})();
/*----------------------------------
  Accordion
  Plugin for Multiple Sets with Auto-Open and Search
  Each function is isolated for modular control
  Used In Asset Browser
-----------------------------------*/
// 1️⃣ Core Accordion Initialization
function initAccordion(options = {}) {
  const defaults = {
    containerSelector: '.accordion',
    groupSelector: '.accordion-group',
    togglerSelector: '.accordion-toggler',
    contentSelector: '.accordion-content',
    singleOpen: true,
    activeClass: 'is-active',
    transitionDuration: 300,
    deferFirstOpen: false
  };

  const settings = { ...defaults, ...options };
  const accordions = document.querySelectorAll(settings.containerSelector);

  accordions.forEach(accordion => {
    const groups = accordion.querySelectorAll(settings.groupSelector);

    groups.forEach((group, index) => {
      const toggler = group.querySelector(settings.togglerSelector);
      const content = group.querySelector(settings.contentSelector);
      if (!toggler || !content) return;

      content.style.overflow = 'hidden';
      content.style.maxHeight = '0';
      content.style.transition = `max-height ${settings.transitionDuration}ms ease`;

      if (index === 0 && !settings.deferFirstOpen) {
        group.classList.add(settings.activeClass);
        content.style.maxHeight = content.scrollHeight + 'px';
      }

      toggler.addEventListener('click', () => {
        const isOpen = group.classList.contains(settings.activeClass);

        if (settings.singleOpen) {
          groups.forEach(g => {
            if (g !== group) {
              g.classList.remove(settings.activeClass);
              const c = g.querySelector(settings.contentSelector);
              c.style.maxHeight = '0';
            }
          });
        }

        if (isOpen) {
          group.classList.remove(settings.activeClass);
          content.style.maxHeight = '0';
        } else {
          group.classList.add(settings.activeClass);
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  });
}

// 2️⃣ Auto-Open First Accordion When Visible
function openFirstAccordionWhenVisibleAll(selector = '.accordion', checkInterval = 100) {
  const accordions = document.querySelectorAll(selector);
  const alreadyOpened = new Set();

  const check = setInterval(() => {
    accordions.forEach(acc => {
      if (!alreadyOpened.has(acc) && acc.offsetParent !== null) {
        const firstGroup = acc.querySelector('.accordion-group');
        const firstContent = acc.querySelector('.accordion-content');
        if (firstGroup && firstContent) {
          firstGroup.classList.add('is-active');
          firstContent.style.maxHeight = firstContent.scrollHeight + 'px';
          alreadyOpened.add(acc);
        }
      }
    });

    if (alreadyOpened.size === accordions.length) {
      clearInterval(check);
    }
  }, checkInterval);
}

// // 3️⃣ Accordion Search (Category Filter)
// function initAccordionSearch(selector = '.dropdown__item-search input') {
//   const searchInputs = document.querySelectorAll(selector);

//   searchInputs.forEach(input => {
//     input.addEventListener('input', e => {
//       const keyword = e.target.value.trim().toLowerCase();
//       const accordion = input.closest('.asset__browser')?.querySelector('.tab-group.is-active .accordion');
//       if (!accordion) return;

//       const groups = accordion.querySelectorAll('.accordion-group');

//       groups.forEach(group => {
//         const toggler = group.querySelector('.accordion-toggler span');
//         const label = toggler?.textContent.toLowerCase() || '';

//         const isMatch = label.includes(keyword);
//         group.style.display = isMatch ? '' : 'none';

//         if (!isMatch) {
//           group.classList.remove('is-active');
//           const content = group.querySelector('.accordion-content');
//           if (content) content.style.maxHeight = '0px';
//         }
//       });
//     });
//   });
// }

// Init 
document.addEventListener('DOMContentLoaded', () => {
  initAccordion({
    containerSelector: '.accordion',
    singleOpen: true,
    deferFirstOpen: true
  });

  openFirstAccordionWhenVisibleAll();
  // initAccordionSearch();
});





/*----------------------------------
    Color Picker
-----------------------------------*/
class DecorColorPicker {
  constructor() {
    if (!document.getElementById('colorCanvas')) {
      return;
    }

    this.currentColor = { h: 0, s: 100, v: 100 };
    this.canvasPosition = { x: 100, y: 100 };
    this.isDragging = false;
    this.dragTarget = null;
    this.currentFormat = 'hex';
    this.lastHex = '';
    this.cachedCanvasRect = null;
    this.cachedHueRect = null;
    this.rafId = null;
    this.preservedHue = 0; // Start with red hue
    this.saveTimeout = null;

    // Default saved colors (white, blue, red)
    this.defaultSavedColors = ['$color-whiteFFF', '#0000FF', '#FF0000'];

    this.initElements();
    this.loadSettings();
    this.bindEvents();
    this.updateDisplay();
    this.renderSavedColors();
    this.syncFormatSelect();
  }

  initElements() {
    this.canvas = document.getElementById('colorCanvas');
    this.canvasPicker = document.getElementById('canvasPicker');
    this.hueBar = document.getElementById('hueBar');
    this.huePicker = document.getElementById('huePicker');
    this.colorInput = document.getElementById('colorInput');
    this.formatSelect = document.getElementById('formatSelect');
    this.addColorBtn = document.getElementById('addColorBtn');
    this.savedColorsContainer = document.getElementById('savedColorsContainer');
  }

  bindEvents() {
    if (!this.canvas || !this.hueBar) return;

    this.canvas.addEventListener('pointerdown', (e) => this.startDrag(e, 'canvas'));
    this.hueBar.addEventListener('pointerdown', (e) => this.startDrag(e, 'hue'));

    document.addEventListener('pointerup', () => this.endDrag());
    document.addEventListener('pointermove', (e) => this.handleDrag(e));

    const resizeObserver = new ResizeObserver(() => this.invalidateCache());
    resizeObserver.observe(this.canvas);
    resizeObserver.observe(this.hueBar);

    this.canvas.addEventListener('keydown', (e) => this.handleKeyboard(e, 'canvas'));
    this.hueBar.addEventListener('keydown', (e) => this.handleKeyboard(e, 'hue'));

    if (this.colorInput) {
      this.colorInput.addEventListener('input', (e) => this.handleColorInput(e));
    }
    if (this.formatSelect) {
      this.formatSelect.addEventListener('change', (e) => this.handleFormatChange(e));
    }
    if (this.addColorBtn) {
      this.addColorBtn.addEventListener('click', () => this.addCurrentColor());
    }
  }


  invalidateCache() {
    this.cachedCanvasRect = null;
    this.cachedHueRect = null;
  }

  getCanvasRect() {
    if (!this.cachedCanvasRect) {
      this.cachedCanvasRect = this.canvas.getBoundingClientRect();
    }
    return this.cachedCanvasRect;
  }

  getHueRect() {
    if (!this.cachedHueRect) {
      this.cachedHueRect = this.hueBar.getBoundingClientRect();
    }
    return this.cachedHueRect;
  }

  startDrag(e, target) {
    e.preventDefault();
    this.isDragging = true;
    this.dragTarget = target;
    this.canvas.setPointerCapture?.(e.pointerId);
    this.hueBar.setPointerCapture?.(e.pointerId);
    this.handleDrag(e);
  }

  handleDrag(e) {
    if (!this.isDragging || this.rafId) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.processDrag(e);
    });
  }

  processDrag(e) {
    if (!this.isDragging) return;

    if (this.dragTarget === 'canvas') {
      const rect = this.getCanvasRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));

      this.canvasPosition.x = (x / rect.width) * 100;
      this.canvasPosition.y = 100 - (y / rect.height) * 100;

      this.currentColor.s = Math.round(this.canvasPosition.x);
      this.currentColor.v = Math.round(this.canvasPosition.y);

    } else if (this.dragTarget === 'hue') {
      const rect = this.getHueRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const hue = Math.round((x / rect.width) * 360);
      this.currentColor.h = hue;
      this.preservedHue = hue; // Always preserve hue
    }

    this.updateDisplay();
    this.emitChangeEvent();
  }

  endDrag() {
    if (this.isDragging) {
      this.isDragging = false;
      this.dragTarget = null;
      this.debouncedSave();
    }
  }

  handleKeyboard(e, target) {
    let step = e.shiftKey ? 10 : 1;
    let changed = false;

    if (target === 'canvas') {
      switch (e.key) {
        case 'ArrowLeft': this.canvasPosition.x = Math.max(0, this.canvasPosition.x - step); changed = true; break;
        case 'ArrowRight': this.canvasPosition.x = Math.min(100, this.canvasPosition.x + step); changed = true; break;
        case 'ArrowUp': this.canvasPosition.y = Math.min(100, this.canvasPosition.y + step); changed = true; break;
        case 'ArrowDown': this.canvasPosition.y = Math.max(0, this.canvasPosition.y - step); changed = true; break;
      }
      if (changed) {
        this.currentColor.s = Math.round(this.canvasPosition.x);
        this.currentColor.v = Math.round(this.canvasPosition.y);
      }
    } else if (target === 'hue') {
      switch (e.key) {
        case 'ArrowLeft': this.currentColor.h = Math.max(0, this.currentColor.h - step); changed = true; break;
        case 'ArrowRight': this.currentColor.h = Math.min(360, this.currentColor.h + step); changed = true; break;
      }
      if (changed) {
        this.preservedHue = this.currentColor.h;
      }
    }

    if (changed) {
      e.preventDefault();
      this.updateDisplay();
      this.emitChangeEvent();
      this.debouncedSave();
    }
  }

  handleColorInput(e) {
    const value = e.target.value.trim();
    let hsv = null;

    if (this.currentFormat === 'hex' && this.isValidHex(value)) {
      hsv = this.hexToHsv(value);
    } else if (this.currentFormat === 'rgb' && this.isValidRgb(value)) {
      const rgb = this.parseRgb(value);
      if (rgb) hsv = this.rgbToHsv(this.clampRgb(rgb));
    } else if (this.currentFormat === 'hsl' && this.isValidHsl(value)) {
      const hsl = this.parseHsl(value);
      if (hsl) hsv = this.hslToHsv(this.clampHsl(hsl));
    }

    if (hsv) {
      // Preserve hue for grays/white
      if (hsv.s === 0 && this.currentColor.s > 0) {
        hsv.h = this.preservedHue;
      } else if (hsv.s > 0) {
        this.preservedHue = hsv.h;
      }

      this.currentColor = hsv;
      this.canvasPosition.x = hsv.s;
      this.canvasPosition.y = hsv.v;
      this.updateDisplay(false); // Don't update input
      this.emitChangeEvent();
      this.debouncedSave();
    }
  }

  handleFormatChange(e) {
    this.currentFormat = e.target.value;
    this.updateColorInput();
    this.debouncedSave();
  }

  selectSavedColor(hex) {
    const hsv = this.hexToHsv(hex);

    // Preserve hue for grays/white
    if (hsv.s === 0 && this.currentColor.s > 0) {
      hsv.h = this.preservedHue;
    } else if (hsv.s > 0) {
      this.preservedHue = hsv.h;
    }

    this.currentColor = hsv;
    this.canvasPosition.x = hsv.s;
    this.canvasPosition.y = hsv.v;
    this.updateDisplay();
    this.emitChangeEvent();
    this.debouncedSave();
  }

  addCurrentColor() {
    const currentHex = this.hsvToHex(this.currentColor).toUpperCase();

    // Check for duplicates (case-insensitive)
    const isDuplicate = this.storage.savedColors.some(
      color => color.toUpperCase() === currentHex
    );

    if (!isDuplicate) {
      this.storage.savedColors.push(currentHex);
      this.renderSavedColors();
      this.debouncedSave();
    }
  }

  removeColor(hex) {
    const index = this.storage.savedColors.findIndex(
      color => color.toUpperCase() === hex.toUpperCase()
    );
    if (index > -1) {
      this.storage.savedColors.splice(index, 1);
      this.renderSavedColors();
      this.debouncedSave();
    }
  }

  renderSavedColors() {
    if (!this.savedColorsContainer || !this.addColorBtn) return;

    const currentHex = this.hsvToHex(this.currentColor).toUpperCase();
    this.savedColorsContainer.innerHTML = '';

    this.storage.savedColors.forEach(hex => {
      const colorDiv = document.createElement('div');
      colorDiv.className = 'colorpicker__saved-color';
      colorDiv.style.backgroundColor = hex;
      colorDiv.setAttribute('role', 'button');
      colorDiv.setAttribute('tabindex', '0');
      colorDiv.setAttribute('aria-label', `Select color ${hex}`);

      if (hex.toUpperCase() === currentHex) {
        colorDiv.classList.add('colorpicker__saved-color--active');
        colorDiv.setAttribute('aria-pressed', 'true');
      } else {
        colorDiv.setAttribute('aria-pressed', 'false');
      }

      colorDiv.addEventListener('click', () => this.selectSavedColor(hex));
      colorDiv.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectSavedColor(hex);
        }
      });

      // Add remove button for ALL colors (including defaults)
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'colorpicker__remove-btn';
      removeBtn.innerHTML = '×';
      removeBtn.setAttribute('aria-label', `Remove color ${hex}`);
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeColor(hex);
      });
      colorDiv.appendChild(removeBtn);

      this.savedColorsContainer.appendChild(colorDiv);
    });

    // Update add button state - disable if current color already exists in saved colors
    const currentHexExists = this.storage.savedColors.some(
      color => color.toUpperCase() === currentHex
    );
    this.addColorBtn.disabled = currentHexExists;

    // Update add button text to show why it's disabled
    if (currentHexExists) {
      this.addColorBtn.textContent = '✓ Added';
      this.addColorBtn.setAttribute('aria-label', 'Color already in saved colors');
    } else {
      this.addColorBtn.textContent = '+ Add';
      this.addColorBtn.setAttribute('aria-label', 'Add current color to saved colors');
    }
  }

  updateDisplay(updateInput = true) {
    if (!this.canvas || !this.canvasPicker || !this.hueBar || !this.huePicker) return;
    const hex = this.hsvToHex(this.currentColor);

    // Only re-render saved colors if the hex changed
    if (hex !== this.lastHex) {
      this.lastHex = hex;
      this.renderSavedColors();
    }

    if (updateInput) {
      this.updateColorInput();
    }

    // Update canvas background with current hue
    const hueColor = this.hsvToHex({ h: this.currentColor.h, s: 100, v: 100 });
    this.canvas.style.background = hueColor;

    // Update canvas picker position
    const pickerX = (this.canvasPosition.x / 100) * 100;
    const pickerY = ((100 - this.canvasPosition.y) / 100) * 100;
    this.canvasPicker.style.left = `${pickerX}%`;
    this.canvasPicker.style.top = `${pickerY}%`;
    this.canvasPicker.style.right = 'auto';

    // Update hue picker position
    const huePosition = (this.currentColor.h / 360) * 100;
    this.huePicker.style.left = `${huePosition}%`;
    this.huePicker.style.right = 'auto';

    // Update ARIA values
    this.canvas.setAttribute('aria-valuenow', `${this.currentColor.s},${this.currentColor.v}`);
    this.hueBar.setAttribute('aria-valuenow', this.currentColor.h);
  }

  updateColorInput() {
    const hex = this.hsvToHex(this.currentColor);

    if (this.currentFormat === 'hex') {
      this.colorInput.value = hex.toUpperCase();
    } else if (this.currentFormat === 'rgb') {
      const rgb = this.hexToRgb(hex);
      this.colorInput.value = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    } else if (this.currentFormat === 'hsl') {
      const hsl = this.hsvToHsl(this.currentColor);
      this.colorInput.value = `hsl(${Math.round(hsl.h)}, ${Math.round(hsl.s)}%, ${Math.round(hsl.l)}%)`;
    }
  }

  syncFormatSelect() {
    this.formatSelect.value = this.currentFormat;
  }

  emitChangeEvent() {
    const event = new CustomEvent('colorchange', {
      detail: {
        hex: this.hsvToHex(this.currentColor),
        rgb: this.hexToRgb(this.hsvToHex(this.currentColor)),
        hsl: this.hsvToHsl(this.currentColor),
        hsv: this.currentColor
      }
    });
    this.canvas.dispatchEvent(event);
  }
  get storageKey() { return 'decor-colorpicker:v1'; }
  loadSettings() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        this.storage = { savedColors: [...this.defaultSavedColors] };
        return;
      }

      const s = JSON.parse(raw);

      // Saved swatches
      this.storage = {
        savedColors: Array.isArray(s.savedColors) && s.savedColors.length
          ? s.savedColors
          : [...this.defaultSavedColors]
      };

      // Saved format
      this.currentFormat = s.currentFormat || 'hex';

      // Saved color + positions
      if (s.currentColor && typeof s.currentColor === 'object') {
        this.currentColor = s.currentColor;
        // keep your hue preservation logic
        this.preservedHue = (typeof s.preservedHue === 'number')
          ? s.preservedHue
          : this.currentColor.h;

        // keep SV cursor aligned with color
        this.canvasPosition.x = this.currentColor.s;
        this.canvasPosition.y = this.currentColor.v;
      }
    } catch (err) {
      // fall back to defaults if anything goes wrong
      this.storage = { savedColors: [...this.defaultSavedColors] };
    }
  }

  debouncedSave() {
    clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(() => this.saveSettings(), 500);
  }

  saveSettings() {
    const settings = {
      savedColors: this.storage.savedColors,
      currentFormat: this.currentFormat,
      currentColor: this.currentColor,
      preservedHue: this.preservedHue
    };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
    } catch (err) {
      // ignore quota / privacy errors silently
    }
  }

  // Validation with proper clamping
  isValidHex(hex) {
    return /^#[0-9A-F]{6}$/i.test(hex);
  }

  isValidRgb(rgb) {
    return /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/i.test(rgb);
  }

  isValidHsl(hsl) {
    return /^hsl\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*\)$/i.test(hsl);
  }

  parseRgb(rgbString) {
    const matches = rgbString.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    return matches ? {
      r: parseInt(matches[1]),
      g: parseInt(matches[2]),
      b: parseInt(matches[3])
    } : null;
  }

  parseHsl(hslString) {
    const matches = hslString.match(/hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/i);
    return matches ? {
      h: parseInt(matches[1]),
      s: parseInt(matches[2]),
      l: parseInt(matches[3])
    } : null;
  }

  clampRgb(rgb) {
    return {
      r: Math.max(0, Math.min(255, rgb.r)),
      g: Math.max(0, Math.min(255, rgb.g)),
      b: Math.max(0, Math.min(255, rgb.b))
    };
  }

  clampHsl(hsl) {
    return {
      h: Math.max(0, Math.min(360, hsl.h)),
      s: Math.max(0, Math.min(100, hsl.s)),
      l: Math.max(0, Math.min(100, hsl.l))
    };
  }

  hexToRgb(hex) {
    return {
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16)
    };
  }

  rgbToHsv(rgb) {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
      if (max === r) h = ((g - b) / delta) % 6;
      else if (max === g) h = (b - r) / delta + 2;
      else h = (r - g) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const s = max === 0 ? 0 : Math.round((delta / max) * 100);
    const v = Math.round(max * 100);

    return { h, s, v };
  }

  hslToHsv(hsl) {
    const h = hsl.h;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    const v = l + s * Math.min(l, 1 - l);
    const sNew = v === 0 ? 0 : 2 * (1 - l / v);

    return {
      h: h,
      s: Math.round(sNew * 100),
      v: Math.round(v * 100)
    };
  }

  hsvToHsl(hsv) {
    const h = hsv.h;
    const s = hsv.s / 100;
    const v = hsv.v / 100;

    const l = v * (1 - s / 2);
    const sNew = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l);

    return {
      h: h,
      s: sNew * 100,
      l: l * 100
    };
  }

  hexToHsv(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
      if (max === r) h = ((g - b) / delta) % 6;
      else if (max === g) h = (b - r) / delta + 2;
      else h = (r - g) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const s = max === 0 ? 0 : Math.round((delta / max) * 100);
    const v = Math.round(max * 100);

    return { h, s, v };
  }

  hsvToHex(hsv) {
    const h = hsv.h / 360;
    const s = hsv.s / 100;
    const v = hsv.v / 100;

    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);

    let r, g, b;
    switch (i % 6) {
      case 0: r = v; g = t; b = p; break;
      case 1: r = q; g = v; b = p; break;
      case 2: r = p; g = v; b = t; break;
      case 3: r = p; g = q; b = v; break;
      case 4: r = t; g = p; b = v; break;
      case 5: r = v; g = p; b = q; break;
    }

    const toHex = (c) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}

// Initialize the color picker
new DecorColorPicker();

/*----------------------------------
    Read More Text
-----------------------------------*/
document.addEventListener("DOMContentLoaded", () => {
  const maxChars = 142;
  const p = document.querySelector(".read-more");
  const btn = document.querySelector(".read-more-toggle");
  if (!p || !btn) return;
  const fullText = p.dataset.fullText || "";
  if (!fullText.length) return;

  const shortText =
    fullText.length > maxChars
      ? fullText.slice(0, maxChars) + "..."
      : fullText;

  let isExpanded = false;
  p.textContent = shortText;

  btn.addEventListener("click", () => {
    isExpanded = !isExpanded;
    p.textContent = isExpanded ? fullText : shortText;
    btn.textContent = isExpanded ? "less" : "more";
  });
});